// src/components/OptimizedImage.js
import React, { useState, useEffect, useCallback } from 'react';
import { imageCache } from '../utils/imageCache';

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  fallback = '/placeholder.jpg', 
  lazy = true,
  ...props 
}) => {
  // Si l'image est déjà en cache, on la charge directement
  const [imageSrc, setImageSrc] = useState(
    imageCache.isImageCached(src) ? src : (lazy ? null : src)
  );
  const [imageRef, setImageRef] = useState();
  const [loaded, setLoaded] = useState(imageCache.isImageCached(src));
  const [error, setError] = useState(false);

  useEffect(() => {
    // Si l'image est en cache, pas besoin d'observer
    if (imageCache.isImageCached(src)) {
      setImageSrc(src);
      setLoaded(true);
      return;
    }

    let observer;
    
    if (imageRef && imageSrc !== src) {
      if (IntersectionObserver && lazy) {
        observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                setImageSrc(src);
                observer.unobserve(imageRef);
              }
            });
          },
          {
            threshold: 0.1,
            rootMargin: '50px'
          }
        );
        observer.observe(imageRef);
      } else {
        setImageSrc(src);
      }
    }
    
    return () => {
      if (observer && observer.unobserve) {
        observer.unobserve(imageRef);
      }
    };
  }, [imageRef, imageSrc, src, lazy]);

  const handleLoad = useCallback(() => {
    setLoaded(true);
    imageCache.cache.set(src, { loaded: true, error: false });
  }, [src]);

  const handleError = useCallback(() => {
    setError(true);
    setImageSrc(fallback);
    imageCache.cache.set(src, { loaded: false, error: true });
  }, [fallback, src]);

  // Préchargement de l'image
  useEffect(() => {
    if (imageSrc && imageSrc !== fallback && !imageCache.isImageCached(imageSrc)) {
      const img = new Image();
      img.onload = handleLoad;
      img.onerror = handleError;
      img.src = imageSrc;
    }
  }, [imageSrc, fallback, handleLoad, handleError]);

  return (
    <div 
      ref={setImageRef}
      className={`optimized-image-container ${className}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'transparent'
      }}
    >
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: loaded && !error ? 1 : 0,
            transition: 'opacity 0.3s ease',
            transform: 'translateZ(0)',
            willChange: 'opacity'
          }}
          {...props}
        />
      )}
      
      {/* Placeholder pendant le chargement */}
      {(!loaded || error) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#2a2a2a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999'
          }}
        >
          {error ? '❌' : '⏳'}
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;